import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { createUri, Api } from '@api/utils';
import {
    type CompanyResponse,
    OrgUrls,
    type NameMap,
    type OrgMap,
    type Countries,
    type LocationMap,
    type LocationResponse,
} from '@api/org';

import type { Entries } from '@/types/general';
import { Query, Mutation } from './utils';

import type { ResellerResponse } from '@/types/resellers';
import type { LocationEmployeeRanks } from '@/types/employees';

const DEFAULT_COMPANY_VALUES = { TierID: 1, ResellerCurrencyCode: 'USD', BillingCycleID: 3 };

enum Tag {
    Company = 'company',
    Location = 'location',
    Reseller = 'reseller',
    NameMap = 'nameMap',
    OrgMap = 'orgMap',
    LocationMap = 'locationMap',
    Countries = 'countries',
    BCCustomers = 'bcCustomers',
}

export const orgHooks = createApi({
    reducerPath: 'orgApi',
    tagTypes: Object.values(Tag),
    baseQuery: fetchBaseQuery({
        baseUrl: createUri(Api.Orgs, '/org/V1'),
    }),
    endpoints: (build) => ({
        Company: build.mutation<CompanyResponse, { company: any; token: string; method: 'POST' | 'PATCH' }>({
            query: ({ company, token, method }) => ({
                url: OrgUrls.Company,
                body: method === 'POST' ? { ...company, ...DEFAULT_COMPANY_VALUES } : company,
                method,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
            invalidatesTags: [Tag.NameMap, Tag.OrgMap, Tag.BCCustomers, Tag.Company],
        }),
        Location: build.mutation<LocationResponse, { location: any; token: string; method: 'POST' | 'PATCH' }>({
            query: ({ location, token, method }) => ({
                url: OrgUrls.Location,
                body: location,
                method,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
            invalidatesTags: [Tag.NameMap, Tag.OrgMap],
        }),
        Reseller: build.mutation<CompanyResponse, { reseller: any; token: string; method: 'POST' | 'PATCH' }>({
            query: ({ reseller, token, method }) => {
                return {
                    url: OrgUrls.Reseller,
                    body: method === 'POST' ? { ...reseller, ...DEFAULT_COMPANY_VALUES } : reseller,
                    method,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                };
            },
            // invalidatesTags: (_, __, { reseller }) => {
            //     const tags: any[] = [Tag.NameMap, Tag.OrgMap];
            //     if (!reseller?.CompanyID) return tags;
            //     tags.push({ name: Tag.Reseller, id: reseller.CompanyID });
            //     return tags;
            // },
            invalidatesTags: [Tag.NameMap, Tag.OrgMap],
        }),
        nameMap: build.query<NameMap, { token: string }>({
            query: ({ token }: { token: string }) => ({
                url: OrgUrls.NameMap,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [Tag.NameMap],
        }),
        orgMap: build.query<OrgMap, { token: string }>({
            query: ({ token }: { token: string }) => ({
                url: OrgUrls.OrgMap,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [Tag.OrgMap],
        }),
        countries: build.query<Countries, { token: string }>({
            query: ({ token }: { token: string }) => ({
                url: OrgUrls.Countries,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [Tag.Countries],
        }),
        locationMap: build.query<{ locations: Entries<LocationMap> }, { token: string }>({
            query: ({ token }: { token: string }) => ({
                url: OrgUrls.LocationMap,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [Tag.LocationMap],
        }),
        reseller: build.query<ResellerResponse, { resellerId: number; token: string }>({
            query: ({ resellerId, token }) => ({
                url: OrgUrls.Reseller,
                params: {
                    CompanyID: resellerId,
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
            // ? Allows for caching single entities, and invalidating
            // ? cache only when they've been updated.
            // ! This can work, but needs more investigation
            // providesTags: (_, err, { resellerId }) => {
            //     if (!err?.status.toString().startsWith('2')) return [];
            //     return [{ type: Tag.Reseller, id: resellerId }];
            // },
            providesTags: [Tag.Reseller],
            keepUnusedDataFor: 0,
        }),
        company: build.query<CompanyResponse, { companyId: number; token: string }>({
            query: ({ companyId, token }) => ({
                url: OrgUrls.Company,
                params: {
                    CompanyID: companyId,
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [Tag.Company],
            keepUnusedDataFor: 0,
        }),
        location: build.query<LocationResponse, { locationId: number; token: string }>({
            query: ({ locationId, token }) => ({
                url: OrgUrls.Location,
                params: {
                    LocationID: locationId,
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [Tag.Location],
            keepUnusedDataFor: 0,
        }),
        bcCustomers: build.query<{ label: string; value: string; }[], { token: string }>({
            query: ({ token }) => ({
                url: OrgUrls.BCCustomers,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [Tag.BCCustomers],
            keepUnusedDataFor: 120,
        }),
        locationEmployeeRanks: build.query<
            LocationEmployeeRanks,
            { token: string; LocationID: number; startDate?: string; endDate?: string }
        >({
            query: ({ token, ...params }) => ({
                url: OrgUrls.EmployeeRanks,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params,
            }),
            keepUnusedDataFor: 60,
        }),
    }),
});

/** Intermediary Wrapper */
const orgs = {
    useNameMapQuery: Query.withToken(orgHooks.useNameMapQuery),
    useOrgMapQuery: Query.withToken(orgHooks.useOrgMapQuery),
    useLocationMapQuery: Query.withToken(orgHooks.useLocationMapQuery),
    useCompanyQuery: Query.withToken(orgHooks.useCompanyQuery),
    useResellerQuery: Query.withToken(orgHooks.useResellerQuery),
    useLocationQuery: Query.withToken(orgHooks.useLocationQuery),
    useCountriesQuery: Query.withToken(orgHooks.useCountriesQuery),
    useCompanyMutation: Mutation.withToken(orgHooks.useCompanyMutation),
    useLocationMutation: Mutation.withToken(orgHooks.useLocationMutation),
    useResellerMutation: Mutation.withToken(orgHooks.useResellerMutation),
    useBcCustomersQuery: Query.withToken(orgHooks.useBcCustomersQuery),
    useLocationEmployeeRanksQuery: Query.withToken(orgHooks.useLocationEmployeeRanksQuery),
};

export const {
    useNameMapQuery,
    useOrgMapQuery,
    useLocationMapQuery,
    useCompanyQuery,
    useResellerQuery,
    useLocationQuery,
    useCountriesQuery,
    useCompanyMutation,
    useLocationMutation,
    useResellerMutation,
    useBcCustomersQuery,
    useLocationEmployeeRanksQuery,
} = orgs;
